/* assets/custom-options-cart-sync.js
   Keeps bundled add-ons in sync with their parent line item.

   - Removes orphan add-ons (addons exist, main missing)
   - Restores missing add-ons (main exists, addon missing) from _co_addon_variant_ids
   - Enforces addon quantities to match main quantity
   - Refreshes NewTheme mini-cart UI (#cart-info, .CartCount, #CartCountCavas)
*/

(function () {
  const BUNDLE_KEY = '_co_bundle';
  const ADDON_FOR = '_co_addon_for';
  const ADDON_VARIANT_IDS = '_co_addon_variant_ids';
  const LEGACY_ADDON_VARIANT_ID = '_co_addon_variant_id';

  let running = false;
  let queued = false;
  let suppressSyncUntil = 0;
  let miniCartAtomicBusy = false;
  let cartPageAtomicBusy = false;
  let cartPageQueuedUpdate = null;
  let extendMarkupRefreshPromise = null;
  let syncCartTimer = null;
  let lastCartAddUiSuccessAt = 0;

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return await res.json();
  }

  async function changeLine(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return await res.json();
  }

  async function updateLines(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return await res.json();
  }

  async function addItem(variantId, quantity, properties) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(quantity || 1),
        properties: properties || {},
      }),
    });
    return await res.json();
  }

  function toNumId(v) {
    const s = String(v || '').trim();
    if (!s) return '';
    const m = s.match(/(\d+)\s*$/);
    return m && m[1] ? m[1] : '';
  }

  function splitIds(csv) {
    if (!csv) return [];
    return String(csv)
      .split(',')
      .map((s) => toNumId(s))
      .filter(Boolean);
  }

  function uniq(arr) {
    const out = [];
    const seen = new Set();
    for (const x of arr) {
      const k = String(x || '');
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(k);
    }
    return out;
  }

  function parseBundles(items) {
    const bundles = new Map();

    (items || []).forEach((item, idx) => {
      const props = item.properties || {};
      const bundle = props[BUNDLE_KEY];
      if (!bundle) return;

      if (!bundles.has(bundle)) bundles.set(bundle, { main: null, addons: [] });

      const entry = bundles.get(bundle);
      if (props[ADDON_FOR]) entry.addons.push({ item, line: idx + 1 });
      else entry.main = { item, line: idx + 1 };
    });

    return bundles;
  }

  function getRequiredAddonVariantIds(mainItem) {
    const props = (mainItem && mainItem.properties) || {};

    const plural = props[ADDON_VARIANT_IDS] ? String(props[ADDON_VARIANT_IDS]).trim() : '';
    let ids = splitIds(plural);

    if (!ids.length) {
      const legacy = props[LEGACY_ADDON_VARIANT_ID] ? String(props[LEGACY_ADDON_VARIANT_ID]).trim() : '';
      ids = splitIds(legacy);
    }

    return uniq(ids);
  }

  function getExistingAddonVariantIds(addonEntries) {
    return uniq((addonEntries || []).map((a) => toNumId(a.item && a.item.id)));
  }

  function updateCartBadges(count) {
    const txt = String(Number(count || 0));
    document.querySelectorAll('.cart-count-badge, .CartCount, #CartCountCavas').forEach((el) => {
      el.textContent = txt;
      el.setAttribute('data-count', txt);
    });
  }

  function replaceMiniCartFooterNode(currentFooter, nextFooter, selector, options) {
    const opts = options || {};
    const currentNode =
      currentFooter.querySelector(selector) ||
      (opts.fallbackSelector ? currentFooter.querySelector(opts.fallbackSelector) : null);
    const nextNode = nextFooter.querySelector(selector);

    if (!currentNode && !nextNode) return;
    if (currentNode && !nextNode) {
      if (!opts.preserveWhenMissing) {
        currentNode.remove();
      }
      return;
    }
    if (!currentNode && nextNode) {
      const clone = nextNode.cloneNode(true);
      if (opts.insertAfterSelector) {
        const insertAfterNode = currentFooter.querySelector(opts.insertAfterSelector);
        if (insertAfterNode && insertAfterNode.parentNode === currentFooter) {
          insertAfterNode.insertAdjacentElement('afterend', clone);
          return;
        }
      }

      const ctaAnchor =
        currentFooter.querySelector('[data-cart-quote-drawer]') ||
        currentFooter.querySelector('.cart__checkout') ||
        currentFooter.querySelector('a[href]');
      if (ctaAnchor && ctaAnchor.parentNode === currentFooter) {
        currentFooter.insertBefore(clone, ctaAnchor);
      } else {
        currentFooter.appendChild(clone);
      }
      return;
    }
    if (!currentNode || !nextNode) return;

    currentNode.replaceWith(nextNode.cloneNode(true));
  }

  function mergeMiniCartMarkup(target, html, preserveFooter) {
    if (!preserveFooter) {
      target.innerHTML = html;
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const nextRoot = doc.querySelector('form.ajaxcart');
    const currentRoot = target.querySelector('form.ajaxcart');

    if (!currentRoot || !nextRoot) {
      target.innerHTML = html;
      return;
    }

    const currentInner = currentRoot.querySelector('.ajaxcart__inner');
    const nextInner = nextRoot.querySelector('.ajaxcart__inner');
    const currentFooter = currentRoot.querySelector('.ajaxcart__footer');
    const nextFooter = nextRoot.querySelector('.ajaxcart__footer');

    if (!currentInner || !nextInner || !currentFooter || !nextFooter) {
      target.innerHTML = html;
      return;
    }

    currentInner.innerHTML = nextInner.innerHTML;
    currentFooter.classList.toggle('h_scroll', nextFooter.classList.contains('h_scroll'));

    replaceMiniCartFooterNode(currentFooter, nextFooter, '.subtotal');
    replaceMiniCartFooterNode(currentFooter, nextFooter, '.cart__savings-row', {
      insertAfterSelector: '.subtotal',
      fallbackSelector: '.total',
    });
    replaceMiniCartFooterNode(currentFooter, nextFooter, '#threshold_bar_popup_minicart');
    replaceMiniCartFooterNode(currentFooter, nextFooter, '.cart_extend--label', { preserveWhenMissing: true });
  }

  async function ensureDrawerExtendMarkup() {
    const drawer = document.getElementById('desktop_cart');
    if (!drawer) return;

    const hasMiniCartForm = !!document.querySelector('#cart-info form.ajaxcart');
    const existingForms = Array.from(drawer.querySelectorAll('.form-cart__extent'));

    if (!hasMiniCartForm) {
      existingForms.forEach((form) => form.remove());
      return;
    }

    if (existingForms.length > 1) {
      existingForms.slice(1).forEach((form) => form.remove());
    }

    const currentForm = drawer.querySelector('.form-cart__extent');
    const hasExtendMarkup = !!(
      currentForm &&
      (currentForm.querySelector('.cart_extend--label') || currentForm.querySelector('.cart_extend'))
    );
    if (currentForm && hasExtendMarkup) {
      return;
    }

    if (extendMarkupRefreshPromise) {
      await extendMarkupRefreshPromise;
      return;
    }

    extendMarkupRefreshPromise = (async function () {
      try {
        const res = await fetch('/cart?view=extend', { headers: { Accept: 'text/html' } });
        if (!res.ok) return;

        const html = await res.text();
        if (!html || !html.trim()) return;

        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        const nextForm = wrapper.querySelector('.form-cart__extent') || wrapper.firstElementChild;
        if (!nextForm) return;

        const current = drawer.querySelector('.form-cart__extent');
        if (current) {
          current.replaceWith(nextForm.cloneNode(true));
          return;
        }

        const anchor = drawer.querySelector('.block_cart_top');
        if (anchor) {
          anchor.parentNode.insertBefore(nextForm.cloneNode(true), anchor);
        } else {
          drawer.insertBefore(nextForm.cloneNode(true), drawer.firstChild);
        }
      } catch (e) {
      } finally {
        extendMarkupRefreshPromise = null;
      }
    })();

    await extendMarkupRefreshPromise;
  }

  async function refreshMiniCartMarkup(options) {
    try {
      const opts = options || {};
      const target = document.getElementById('cart-info');
      if (!target) return;
      const drawer = document.getElementById('desktop_cart');
      const wasDrawerOpen = !!(drawer && drawer.classList.contains('active'));

      const res = await fetch('/cart?view=json', { headers: { Accept: 'text/html' } });
      if (!res.ok) return;
      const html = await res.text();

      mergeMiniCartMarkup(target, html, !!opts.preserveFooter);
      await ensureDrawerExtendMarkup();

      if (!opts.skipDrawerUpdatedEvent) {
        window.dispatchEvent(
          new CustomEvent('co.cart.drawer.updated', {
            detail: {
              source: 'custom-options-cart-sync',
              preserveFooter: !!opts.preserveFooter,
            },
          })
        );
      }

      if (wasDrawerOpen && drawer) {
        drawer.classList.add('active');
        document.querySelectorAll('.sidebar-overlay').forEach((overlay) => {
          overlay.classList.add('act');
        });
      }
    } catch (e) {}
  }

  async function fetchCartPageSectionMarkup() {
    const res = await fetch('/cart', { headers: { Accept: 'text/html' } });
    if (!res.ok) return null;

    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('[data-section-type="cart-template"]');
  }

  function swapCartPageNode(currentSection, nextSection, selector) {
    const currentNode = currentSection.querySelector(selector);
    const nextNode = nextSection.querySelector(selector);

    if (!currentNode && !nextNode) return;
    if (currentNode && !nextNode) {
      currentNode.remove();
      return;
    }
    if (!currentNode || !nextNode) return;

    currentNode.innerHTML = nextNode.innerHTML;
  }

  async function refreshCartPageMarkup(updatedCart) {
    const currentSection = document.querySelector('[data-section-type="cart-template"]');
    if (!currentSection) return;

    const itemCount = Number(updatedCart && updatedCart.item_count ? updatedCart.item_count : NaN);
    if (Number.isFinite(itemCount) && itemCount <= 0) {
      window.location.reload();
      return;
    }

    const nextSection = await fetchCartPageSectionMarkup();
    if (!nextSection) {
      window.location.reload();
      return;
    }

    const currentBody = currentSection.querySelector('.cart__body');
    const nextBody = nextSection.querySelector('.cart__body');

    if (!currentBody || !nextBody) {
      window.location.reload();
      return;
    }

    currentBody.innerHTML = nextBody.innerHTML;
    swapCartPageNode(currentSection, nextSection, '.cart__layout_related');
    swapCartPageNode(currentSection, nextSection, '.cart__layout_right .cart__heading');
    swapCartPageNode(currentSection, nextSection, '.cart__layout_right .grid');
  }

  async function refreshCartPageSection() {
    try {
      const currentSection = document.querySelector('[data-section-type="cart-template"]');
      if (!currentSection) return;

      const drawerIsOpen = !!(
        document.getElementById('desktop_cart') &&
        document.getElementById('desktop_cart').classList.contains('active')
      );

      // Keep drawer interactions smooth: do not force a full page reload while drawer is open.
      if (drawerIsOpen) return;

      await refreshCartPageMarkup();
    } catch (e) {}
  }

  async function refreshThemeCartUI() {
    try {
      const cart = await fetchCart();
      updateCartBadges(cart && cart.item_count ? cart.item_count : 0);
      window.dispatchEvent(new CustomEvent('co.cart.updated', { detail: cart }));
      window.dispatchEvent(new CustomEvent('kt.cart.updated', { detail: cart }));
    } catch (e) {}

    await refreshMiniCartMarkup();
    await refreshCartPageSection();
  }

  async function syncCart() {
    if (Date.now() < suppressSyncUntil) {
      return;
    }

    if (running) {
      queued = true;
      return;
    }

    running = true;

    try {
      const cart = await fetchCart();
      const bundles = parseBundles(cart.items);

      const removals = [];
      const qtyFixes = [];
      const restorations = [];

      bundles.forEach((entry, bundleKey) => {
        const main = entry.main;
        const addons = entry.addons || [];

        if (!main && addons.length) {
          addons.forEach((a) => removals.push({ line: a.line }));
          return;
        }

        if (!main) return;

        const mainQty = Number(main.item.quantity || 0);
        if (mainQty <= 0) {
          addons.forEach((a) => removals.push({ line: a.line }));
          return;
        }

        addons.forEach((a) => {
          const addonQty = Number(a.item.quantity || 0);
          if (addonQty !== mainQty) qtyFixes.push({ line: a.line, quantity: mainQty });
        });

        const requiredIds = getRequiredAddonVariantIds(main.item);
        if (!requiredIds.length) return;

        const existingIds = getExistingAddonVariantIds(addons);
        const missing = requiredIds.filter((id) => existingIds.indexOf(id) === -1);

        missing.forEach((variantId) => {
          restorations.push({
            variantId,
            quantity: mainQty,
            properties: {
              [BUNDLE_KEY]: bundleKey,
              [ADDON_FOR]: 'Add-on',
            },
          });
        });
      });

      removals.sort((a, b) => b.line - a.line);
      qtyFixes.sort((a, b) => b.line - a.line);

      for (const r of removals) {
        await changeLine(r.line, 0);
      }

      for (const q of qtyFixes) {
        await changeLine(q.line, q.quantity);
      }

      for (const add of restorations) {
        await addItem(add.variantId, add.quantity, add.properties);
      }

      if (removals.length || qtyFixes.length || restorations.length) {
        await refreshThemeCartUI();

        window.dispatchEvent(
          new CustomEvent('co.cart.sync', {
            detail: {
              removals: removals.length,
              qtyFixes: qtyFixes.length,
              restorations: restorations.length,
            },
          })
        );
      }
    } catch (e) {
      console.warn('[custom-options-cart-sync] sync failed:', e);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        syncCart();
      }
    }
  }

  window.CustomOptionsCartSync = { syncCart };

  function queueSyncCart(delay) {
    if (Date.now() < suppressSyncUntil) {
      return;
    }

    const nextDelay = Number.isFinite(Number(delay)) ? Math.max(0, Number(delay)) : 40;
    if (syncCartTimer) {
      clearTimeout(syncCartTimer);
    }

    syncCartTimer = setTimeout(function () {
      syncCartTimer = null;
      syncCart();
    }, nextDelay);
  }

  async function normalizeMiniCartMarkupOnLoad() {
    try {
      if (!document.getElementById('cart-info')) return;
      await refreshMiniCartMarkup({ preserveFooter: true, skipDrawerUpdatedEvent: true });
    } catch (e) {}
  }

  function initCartSync() {
    syncCart();
    normalizeMiniCartMarkupOnLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartSync);
  } else {
    initCartSync();
  }

  window.addEventListener('co.cart.added', function () {
    queueSyncCart(20);
  });
  window.addEventListener('co.cart.updated', function () {
    queueSyncCart(80);
  });
  window.addEventListener('kt.cart.added', function () {
    queueSyncCart(20);
  });
  window.addEventListener('kt.cart.updated', function () {
    queueSyncCart(80);
  });

  function shouldOpenMiniCartAfterAdd() {
    const mode = window.theme && window.theme.cart_status ? String(window.theme.cart_status) : '';
    return mode !== 'goto_page_cart';
  }

  function openMiniCartDrawer() {
    const drawer = document.getElementById('desktop_cart');
    if (drawer) {
      drawer.classList.add('active');
    }

    document.querySelectorAll('.sidebar-overlay').forEach((overlay) => {
      overlay.classList.add('act');
    });
  }

  function clearProductAddToCartBusyState(targetBtn) {
    const buttons = targetBtn ? [targetBtn] : Array.from(document.querySelectorAll('.product-form__cart-submit'));

    buttons.forEach(function (btn) {
      if (!btn) return;
      btn.classList.remove('loading');
      if (btn.style && btn.style.pointerEvents === 'none') {
        btn.style.pointerEvents = '';
      }
      if (document.activeElement === btn && typeof btn.blur === 'function') {
        btn.blur();
      }
    });
  }

  function handleCartAddUiSuccess() {
    const now = Date.now();
    if (now - lastCartAddUiSuccessAt < 220) {
      return;
    }
    lastCartAddUiSuccessAt = now;

    clearProductAddToCartBusyState();
    if (shouldOpenMiniCartAfterAdd()) {
      setTimeout(openMiniCartDrawer, 120);
    }
  }

  window.addEventListener('co.cart.added', handleCartAddUiSuccess);
  window.addEventListener('kt.cart.added', handleCartAddUiSuccess);

  if (window.jQuery) {
    window.jQuery(document).on('ajaxComplete', function (_event, _xhr, settings) {
      const url = (settings && settings.url) ? String(settings.url) : '';
      if (!url) return;

      if (url.indexOf('/cart/add') !== -1) {
        clearProductAddToCartBusyState();

        if (_xhr && _xhr.status >= 200 && _xhr.status < 300 && shouldOpenMiniCartAfterAdd()) {
          setTimeout(openMiniCartDrawer, 120);
        }
      }

      if (
        url.indexOf('/cart/add') !== -1 ||
        url.indexOf('/cart/change') !== -1 ||
        url.indexOf('/cart/update') !== -1
      ) {
        queueSyncCart(40);
      }
    });
  }

  document.addEventListener('click', function (event) {
    const addBtn = event.target && event.target.closest ? event.target.closest('.product-form__cart-submit') : null;
    if (!addBtn) return;

    if (document.activeElement === addBtn && typeof addBtn.blur === 'function') {
      addBtn.blur();
    }

    setTimeout(function () {
      clearProductAddToCartBusyState(addBtn);
    }, 2400);
  }, true);

  function clampQuantity(rawQty, min, max) {
    let qty = Number(rawQty);
    const minQty = Number.isFinite(min) ? min : 1;
    const maxQty = Number.isFinite(max) && max > 0 ? max : Infinity;

    if (!Number.isFinite(qty)) qty = minQty;
    if (qty < 0) qty = 0;
    if (qty > 0 && qty < minQty) qty = minQty;
    if (qty > maxQty) qty = maxQty;

    return Math.floor(qty);
  }

  function setMiniCartBusyState(isBusy) {
    const cartInfo = document.getElementById('cart-info');
    if (cartInfo) {
      cartInfo.classList.toggle('loading', !!isBusy);
    }

    document.querySelectorAll('#cart-info .cart__mini-qty, #cart-info .remove-from-cart').forEach((el) => {
      el.style.pointerEvents = isBusy ? 'none' : '';
    });

    document.querySelectorAll('#cart-info .cart__mini-qty--input').forEach((el) => {
      if (el.type === 'hidden') return;
      el.disabled = !!isBusy;
    });
  }

  function setCartPageBusyState(isBusy) {
    const section = document.querySelector('[data-section-type="cart-template"]');
    if (!section) return;

    section.classList.toggle('loading', !!isBusy);

    section.querySelectorAll('.cart__qty .plus, .cart__qty .minus, .cart__remove').forEach((el) => {
      el.style.pointerEvents = isBusy ? 'none' : '';
    });

    section.querySelectorAll('.cart__qty-input').forEach((el) => {
      if (el.type === 'hidden') return;
      el.disabled = !!isBusy;
    });
  }

  async function applyMiniCartAtomicLineUpdate(line, quantity) {
    if (miniCartAtomicBusy) return;

    miniCartAtomicBusy = true;
    setMiniCartBusyState(true);

    try {
      const cart = await fetchCart();
      const lineIndex = Number(line) - 1;
      if (!Number.isFinite(lineIndex) || lineIndex < 0 || lineIndex >= (cart.items || []).length) {
        return;
      }

      const items = cart.items || [];
      const targetItem = items[lineIndex];
      const targetProps = (targetItem && targetItem.properties) || {};
      const targetBundle = targetProps[BUNDLE_KEY] ? String(targetProps[BUNDLE_KEY]) : '';
      const nextQty = Math.max(0, Math.floor(Number(quantity) || 0));
      const updates = items.map((item) => Number(item.quantity || 0));

      if (targetBundle) {
        items.forEach((item, idx) => {
          const props = (item && item.properties) || {};
          if (String(props[BUNDLE_KEY] || '') === targetBundle) {
            updates[idx] = nextQty;
          }
        });
      } else {
        updates[lineIndex] = nextQty;
      }

      const updatedCart = await updateLines(updates);
      suppressSyncUntil = Date.now() + 800;

      updateCartBadges(updatedCart && updatedCart.item_count ? updatedCart.item_count : 0);
      window.dispatchEvent(new CustomEvent('co.cart.updated', { detail: updatedCart }));
      window.dispatchEvent(new CustomEvent('kt.cart.updated', { detail: updatedCart }));
      await refreshMiniCartMarkup({ preserveFooter: true });
    } catch (e) {
      console.warn('[custom-options-cart-sync] mini-cart atomic update failed:', e);
    } finally {
      miniCartAtomicBusy = false;
      setMiniCartBusyState(false);
    }
  }

  function getCartPageLineFromRemoveButton(removeBtn) {
    const row = removeBtn ? removeBtn.closest('.cart_item') : null;
    const rowInput = row ? row.querySelector('.cart__qty-input[data-line]') : null;
    const lineFromInput = rowInput ? Number(rowInput.getAttribute('data-line')) : NaN;
    if (Number.isFinite(lineFromInput)) return lineFromInput;

    const href = removeBtn ? String(removeBtn.getAttribute('href') || '') : '';
    const lineMatch = href.match(/[?&]line=(\d+)/);
    return lineMatch && lineMatch[1] ? Number(lineMatch[1]) : NaN;
  }

  async function applyCartPageAtomicLineUpdate(line, quantity) {
    const normalizedLine = Number(line);
    const normalizedQty = Math.max(0, Math.floor(Number(quantity) || 0));

    if (!Number.isFinite(normalizedLine) || normalizedLine < 1) {
      return;
    }

    if (cartPageAtomicBusy) {
      cartPageQueuedUpdate = { line: normalizedLine, quantity: normalizedQty };
      return;
    }

    cartPageAtomicBusy = true;
    setCartPageBusyState(true);

    try {
      const cart = await fetchCart();
      const items = cart.items || [];
      const lineIndex = normalizedLine - 1;
      if (lineIndex < 0 || lineIndex >= items.length) {
        return;
      }

      const targetItem = items[lineIndex];
      const targetProps = (targetItem && targetItem.properties) || {};
      const targetBundle = targetProps[BUNDLE_KEY] ? String(targetProps[BUNDLE_KEY]) : '';
      const updates = items.map((item) => Number(item.quantity || 0));

      if (targetBundle) {
        items.forEach((item, idx) => {
          const props = (item && item.properties) || {};
          if (String(props[BUNDLE_KEY] || '') === targetBundle) {
            updates[idx] = normalizedQty;
          }
        });
      } else {
        updates[lineIndex] = normalizedQty;
      }

      const updatedCart = await updateLines(updates);
      suppressSyncUntil = Date.now() + 900;

      updateCartBadges(updatedCart && updatedCart.item_count ? updatedCart.item_count : 0);
      window.dispatchEvent(new CustomEvent('co.cart.updated', { detail: updatedCart }));
      window.dispatchEvent(new CustomEvent('kt.cart.updated', { detail: updatedCart }));

      await Promise.all([refreshMiniCartMarkup({ preserveFooter: true }), refreshCartPageMarkup(updatedCart)]);
    } catch (e) {
      console.warn('[custom-options-cart-sync] cart-page atomic update failed:', e);
      try {
        window.location.reload();
      } catch (_reloadError) {}
    } finally {
      cartPageAtomicBusy = false;
      setCartPageBusyState(false);

      if (cartPageQueuedUpdate) {
        const queuedUpdate = cartPageQueuedUpdate;
        cartPageQueuedUpdate = null;
        applyCartPageAtomicLineUpdate(queuedUpdate.line, queuedUpdate.quantity);
      }
    }
  }

  document.addEventListener('click', function (event) {
    const qtyButton = event.target && event.target.closest ? event.target.closest('#cart-info .cart__mini-qty') : null;
    if (qtyButton) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const qtyWrap = qtyButton.closest('.cart__mini--qty');
      const qtyInput = qtyWrap ? qtyWrap.querySelector('.cart__mini-qty--input') : null;
      const line = qtyInput ? Number(qtyInput.getAttribute('data-line')) : NaN;
      const current = qtyInput ? Number(qtyInput.value) : NaN;
      const step = qtyInput ? Number(qtyInput.getAttribute('step') || 1) : 1;
      const min = qtyInput ? Number(qtyInput.getAttribute('min') || 1) : 1;
      const maxRaw = qtyInput ? Number(qtyInput.getAttribute('max')) : NaN;
      const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;

      if (!Number.isFinite(line) || !Number.isFinite(current)) {
        return;
      }

      const isPlus = qtyButton.classList.contains('cart__mini-qty--plus');
      const rawNext = isPlus ? current + step : current - step;
      const next = clampQuantity(rawNext, min, max);
      if (next === current) return;

      applyMiniCartAtomicLineUpdate(line, next);
      return;
    }

    const removeBtn = event.target && event.target.closest ? event.target.closest('#cart-info .remove-from-cart') : null;
    if (removeBtn) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const line = Number(removeBtn.getAttribute('data-line'));
      if (!Number.isFinite(line)) return;
      applyMiniCartAtomicLineUpdate(line, 0);
      return;
    }

    const pageQtyButton = event.target && event.target.closest ? event.target.closest('.plus, .minus') : null;
    if (pageQtyButton) {
      const cartSection = pageQtyButton.closest('[data-section-type="cart-template"]');
      if (!cartSection) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const qtyWrap = pageQtyButton.closest('.cart__qty');
      const qtyInput = qtyWrap ? qtyWrap.querySelector('.cart__qty-input') : null;
      if (!qtyInput || qtyInput.type === 'hidden') return;

      const line = Number(qtyInput.getAttribute('data-line'));
      const current = Number(qtyInput.value);
      const step = Number(qtyInput.getAttribute('step') || 1);
      const min = Number(qtyInput.getAttribute('min') || 1);
      const maxRaw = Number(qtyInput.getAttribute('max'));
      const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;
      if (!Number.isFinite(line) || !Number.isFinite(current)) return;

      const isPlus = pageQtyButton.classList.contains('plus');
      const rawNext = isPlus ? current + step : current - step;
      const next = clampQuantity(rawNext, min, max);
      if (next === current) return;

      applyCartPageAtomicLineUpdate(line, next);
      return;
    }

    const pageRemoveBtn = event.target && event.target.closest ? event.target.closest('.cart__remove') : null;
    if (pageRemoveBtn) {
      const cartSection = pageRemoveBtn.closest('[data-section-type="cart-template"]');
      if (!cartSection) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const line = getCartPageLineFromRemoveButton(pageRemoveBtn);
      if (!Number.isFinite(line)) return;
      applyCartPageAtomicLineUpdate(line, 0);
    }
  }, true);

  document.addEventListener('change', function (event) {
    const qtyInput = event.target && event.target.closest ? event.target.closest('#cart-info .cart__mini-qty--input') : null;
    if (!qtyInput || qtyInput.type === 'hidden') {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const line = Number(qtyInput.getAttribute('data-line'));
    const min = Number(qtyInput.getAttribute('min') || 1);
    const maxRaw = Number(qtyInput.getAttribute('max'));
    const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;
    const next = clampQuantity(qtyInput.value, min, max);
    qtyInput.value = String(next > 0 ? next : 1);

    if (!Number.isFinite(line)) return;
    applyMiniCartAtomicLineUpdate(line, next);
    return;
  }, true);

  document.addEventListener('change', function (event) {
    const qtyInput = event.target && event.target.closest ? event.target.closest('.cart__qty-input') : null;
    if (!qtyInput || qtyInput.type === 'hidden') {
      return;
    }

    const cartSection = qtyInput.closest('[data-section-type="cart-template"]');
    if (!cartSection) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const line = Number(qtyInput.getAttribute('data-line'));
    const min = Number(qtyInput.getAttribute('min') || 1);
    const maxRaw = Number(qtyInput.getAttribute('max'));
    const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;
    const next = clampQuantity(qtyInput.value, min, max);
    qtyInput.value = String(next > 0 ? next : 0);

    if (!Number.isFinite(line)) return;
    applyCartPageAtomicLineUpdate(line, next);
  }, true);

  document.addEventListener('keyup', function (event) {
    const qtyInput = event.target && event.target.closest ? event.target.closest('.cart__qty-input') : null;
    if (!qtyInput || qtyInput.type === 'hidden') {
      return;
    }

    const cartSection = qtyInput.closest('[data-section-type="cart-template"]');
    if (!cartSection) {
      return;
    }

    event.stopImmediatePropagation();

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    const line = Number(qtyInput.getAttribute('data-line'));
    const min = Number(qtyInput.getAttribute('min') || 1);
    const maxRaw = Number(qtyInput.getAttribute('max'));
    const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;
    const next = clampQuantity(qtyInput.value, min, max);
    qtyInput.value = String(next > 0 ? next : 0);

    if (!Number.isFinite(line)) return;
    applyCartPageAtomicLineUpdate(line, next);
  }, true);
})();
